import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SlackLogo } from '@/components/ui/slack-logo'
import { Switch } from '@/components/ui/switch'
import { useAccountType } from '@/hooks/useAccountType'
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications'
import { useSlackUserLink, useSlackWorkspace } from '@/hooks/useIntegrations'
import { notificationsApi } from '@/lib/api/notifications.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import {
  BellIcon,
  BellRingingIcon,
  CheckIcon,
  CircleNotchIcon,
  EnvelopeIcon,
  MonitorIcon,
  SpeakerHighIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import {
  isNotificationChannelDefaultEnabled,
  NotificationChannel,
  NotificationPreference,
  NotificationType,
  UpdateNotificationPreferenceDto,
} from '@scrambl/shared'

const logger = createLogger('components/notifications/NotificationPreferences')

interface NotificationPreferencesProps {
  className?: string
  notificationTypes?: readonly NotificationTypeConfig[]
  channelFilter?: readonly (NotificationChannel | 'BROWSER')[]
  getDefaultPreferenceValue?: (
    channel: NotificationChannel,
    type: NotificationType,
  ) => boolean
  loadPreferences?: () => Promise<NotificationPreference[]>
  updateMultiplePreferences?: (data: {
    preferences: UpdateNotificationPreferenceDto[]
  }) => Promise<unknown>
  resetPreferences?: () => Promise<unknown>
  showResetToDefaults?: boolean
  // Hide the permission banner + "Channels & devices" panel — for embeds
  // (e.g. the project-watcher section) that only manage the types grid
  showChannelSettings?: boolean
}

// Notification type configurations
export interface NotificationTypeConfig {
  type: NotificationType
  label: string
  description: string
  category: string
  channels?: readonly (NotificationChannel | 'BROWSER')[]
}

export const NOTIFICATION_TYPES = [
  {
    type: NotificationType.TASK_ASSIGNED,
    label: 'Task Assignments',
    description: 'When you are assigned to a task',
    category: 'Tasks',
  },
  {
    type: NotificationType.TASK_DUE_SOON,
    label: 'Task Due Soon',
    description: 'When your tasks are approaching their due date',
    category: 'Tasks',
  },
  {
    type: NotificationType.TASK_OVERDUE,
    label: 'Overdue Tasks',
    description: 'When your tasks become overdue',
    category: 'Tasks',
  },
  {
    type: NotificationType.TICKET_CREATED,
    label: 'New Tickets',
    description:
      'When a ticket arrives in an inbox you are notified on, or is assigned to you',
    category: 'Tickets',
  },
  {
    type: NotificationType.TICKET_ASSIGNED,
    label: 'Ticket Assignments',
    description: 'When you are assigned to a ticket',
    category: 'Tickets',
  },
  {
    type: NotificationType.TICKET_CUSTOMER_REPLY,
    label: 'Customer Replies',
    description:
      'When a customer replies to a ticket by email or through the portal',
    category: 'Tickets',
  },
  {
    type: NotificationType.TICKET_STATUS_CHANGED,
    label: 'Ticket Status Changes',
    description: 'When a ticket you are on moves to a different stage',
    category: 'Tickets',
  },
  {
    type: NotificationType.TICKET_COMMENT_ADDED,
    label: 'Ticket Comments',
    description: 'When someone comments on a ticket you are on',
    category: 'Tickets',
  },
  {
    type: NotificationType.COMMENT_MENTION,
    label: 'Comment Mentions',
    description: 'When someone mentions you in a comment',
    category: 'Comments',
  },
  {
    type: NotificationType.NOTE_MENTION,
    label: 'Note Mentions',
    description: 'When someone mentions you in a note',
    category: 'Notes',
  },
  {
    type: NotificationType.COMMENT_REPLY,
    label: 'Comment Replies',
    description: 'When someone replies to your comment',
    category: 'Comments',
  },
  {
    type: NotificationType.PROJECT_MEMBER_ADDED,
    label: 'Project Invitations',
    description: 'When you are added to a project',
    category: 'Projects',
  },
  {
    type: NotificationType.INVOICE_SENT,
    label: 'Invoice Sent',
    description: 'When an invoice is sent to a client',
    category: 'Invoices',
  },
  {
    type: NotificationType.INVOICE_OPENED,
    label: 'Invoice Opened',
    description: 'When a client opens an invoice you sent',
    category: 'Invoices',
  },
  {
    type: NotificationType.INVOICE_PAYMENT_RECORDED,
    label: 'Payment Recorded',
    description: 'When a payment is recorded on an invoice',
    category: 'Invoices',
  },
  {
    type: NotificationType.INVOICE_OVERDUE,
    label: 'Invoice Overdue',
    description: 'When an invoice you sent passes its due date unpaid',
    category: 'Invoices',
  },
  {
    type: NotificationType.SYSTEM_MAINTENANCE,
    label: 'System Updates',
    description: 'Important system announcements and maintenance notices',
    category: 'System',
  },
  {
    type: NotificationType.RESOURCE_WEEKLY_DIGEST,
    label: 'Weekly Digest',
    description: 'A Monday summary of your allocations and utilization',
    category: 'Resources',
    channels: [NotificationChannel.EMAIL],
  },
  {
    type: NotificationType.TIME_ENTRY_TAGGED,
    label: 'Tagged on Time Entries',
    description:
      'When a teammate tags you on their time entry so you can add it to your own timesheet',
    category: 'Time',
  },
  {
    type: NotificationType.ENTITY_HEALTH_AT_RISK,
    label: 'Client At Risk',
    description: 'When a client you are on drops into the at-risk health band',
    category: 'Client Health',
  },
  {
    type: NotificationType.ENTITY_HEALTH_DECLINED,
    label: 'Client Health Declining',
    description: 'When a client slips from healthy to the watch band',
    category: 'Client Health',
  },
  {
    type: NotificationType.ENTITY_HEALTH_IMPROVED,
    label: 'Client Health Improved',
    description: 'When a client recovers from a lower health band',
    category: 'Client Health',
  },
  {
    type: NotificationType.DEAL_HEALTH_AT_RISK,
    label: 'Deal At Risk',
    description: 'When a deal you own drops into the risk health band',
    category: 'Client Health',
  },
  {
    type: NotificationType.LIFECYCLE_STAGE_AUTOMATED,
    label: 'Automated Stage Changes',
    description:
      'When automation moves a client or contact lifecycle stage (won deal, health change)',
    category: 'Client Health',
  },
] as const satisfies readonly NotificationTypeConfig[]

const DEFAULT_DESKTOP_SETTINGS = {
  showBadgeCount: true,
  soundName: 'default',
  enableActions: true,
  persistNotifications: false,
  groupNotifications: true,
} as const

export const NotificationPreferences: FC<NotificationPreferencesProps> = ({
  className,
  notificationTypes = NOTIFICATION_TYPES,
  channelFilter,
  getDefaultPreferenceValue,
  loadPreferences: loadPreferencesProp,
  updateMultiplePreferences,
  resetPreferences,
  showResetToDefaults = true,
  showChannelSettings = true,
}) => {
  const { isPersonal } = useAccountType()
  const [preferences, setPreferences] = useState<NotificationPreference[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {},
  )
  const [isTesting, setIsTesting] = useState(false)

  // Browser notification integration
  const {
    preferences: browserPrefs,
    isSupported: browserSupported,
    permissionStatus,
    enableBrowserNotifications,
    disableBrowserNotifications,
    updateNotificationTypes,
    updateDisplaySettings,
    updateDesktopSettings,
    requestPermission,
    isDesktopApp,
    // This hook also sets up the event listeners for browser notifications
  } = useBrowserNotifications()

  const desktopSettings = {
    ...DEFAULT_DESKTOP_SETTINGS,
    ...(browserPrefs.desktopSettings || {}),
  }

  const desktopLabel = isDesktopApp ? 'Desktop' : 'Browser'
  const desktopDescription = isDesktopApp
    ? 'Native notifications from the desktop app'
    : 'Desktop notifications from your browser'
  const desktopColor = isDesktopApp ? 'text-amber-600' : 'text-purple-600'

  // Slack DMs are only offered once the org workspace is connected with
  // notifications enabled AND this user linked their Slack account.
  const { data: slackWorkspace } = useSlackWorkspace({ enabled: !isPersonal })
  const { data: slackUserLink } = useSlackUserLink(undefined, {
    enabled:
      !isPersonal &&
      !!slackWorkspace &&
      slackWorkspace.status === 'ACTIVE' &&
      slackWorkspace.settings.notificationsEnabled !== false,
  })
  const slackAvailable =
    !isPersonal &&
    !!slackWorkspace &&
    slackWorkspace.status === 'ACTIVE' &&
    slackWorkspace.settings.notificationsEnabled !== false &&
    !!slackUserLink

  const allChannels = [
    {
      channel: NotificationChannel.IN_APP,
      label: 'In-App',
      description: 'Notifications within the application',
      icon: BellIcon,
      color: 'text-blue-600',
    },
    {
      channel: NotificationChannel.EMAIL,
      label: 'Email',
      description: 'Email notifications to your inbox',
      icon: EnvelopeIcon,
      color: 'text-green-600',
    },
    {
      channel: 'BROWSER' as const,
      label: desktopLabel,
      description: desktopDescription,
      icon: MonitorIcon,
      color: desktopColor,
    },
    ...(slackAvailable
      ? [
          {
            channel: NotificationChannel.SLACK,
            label: 'Slack',
            description: 'Direct messages from the Scrambl Slack app',
            icon: SlackLogo,
            color: 'text-[#611f69]',
          },
        ]
      : []),
  ]
  const channels = channelFilter
    ? allChannels.filter(channelConfig =>
        channelFilter.includes(channelConfig.channel),
      )
    : allChannels
  const typeGridTemplateColumns = `minmax(0,1fr) repeat(${channels.length},56px)`

  // Map notification types to browser preference keys
  const getBrowserTypeKey = (
    notificationType: NotificationType,
  ): string | null => {
    const typeMap: Record<string, string> = {
      [NotificationType.TASK_ASSIGNED]: 'assignments',
      [NotificationType.TASK_STATUS_CHANGED]: 'tasks',
      [NotificationType.TASK_DUE_SOON]: 'deadlines',
      [NotificationType.TASK_OVERDUE]: 'deadlines',
      [NotificationType.TASK_COMMENT_ADDED]: 'comments',
      [NotificationType.TICKET_CREATED]: 'tasks',
      [NotificationType.TICKET_ASSIGNED]: 'assignments',
      [NotificationType.TICKET_UPDATED]: 'tasks',
      [NotificationType.TICKET_STATUS_CHANGED]: 'tasks',
      [NotificationType.TICKET_COMMENT_ADDED]: 'comments',
      [NotificationType.TICKET_CUSTOMER_REPLY]: 'comments',
      [NotificationType.COMMENT_MENTION]: 'mentions',
      [NotificationType.NOTE_MENTION]: 'mentions',
      [NotificationType.COMMENT_REPLY]: 'comments',
      [NotificationType.PROJECT_MEMBER_ADDED]: 'tasks',
      [NotificationType.SYSTEM_MAINTENANCE]: 'system',
    }
    return typeMap[notificationType] || null
  }

  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await (loadPreferencesProp
        ? loadPreferencesProp()
        : notificationsApi.getPreferences())
      setPreferences(data)
    } catch (error) {
      logger.error('Error loading notification preferences:', error)
      toast.error('Failed to load notification preferences')
    } finally {
      setIsLoading(false)
    }
  }, [loadPreferencesProp])

  // Load preferences on mount
  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  // Get preference value for a channel/type combination
  const getPreferenceValue = (
    channel: NotificationChannel | 'BROWSER',
    type: NotificationType,
  ): boolean => {
    const key = `${channel}-${type}`

    // Check pending changes first
    if (key in pendingChanges) {
      return pendingChanges[key]
    }

    // Handle browser notifications
    if (channel === 'BROWSER') {
      if (!browserSupported || !browserPrefs.enabled) return false

      const browserTypeKey = getBrowserTypeKey(type)
      if (!browserTypeKey) return false

      return (
        browserPrefs.enabledTypes?.[
          browserTypeKey as keyof typeof browserPrefs.enabledTypes
        ] ?? true
      )
    }

    // Find existing preference for regular channels
    const existing = preferences.find(
      p => p.channel === channel && p.type === type,
    )

    if (existing) {
      return existing.enabled
    }

    if (getDefaultPreferenceValue) {
      return getDefaultPreferenceValue(channel, type)
    }

    // Shared with the API's preference resolution so an untouched toggle shows
    // what delivery will actually do. These used to be two hand-maintained
    // lists and had drifted apart.
    return isNotificationChannelDefaultEnabled(channel, type)
  }

  // Handle preference change
  const handlePreferenceChange = (
    channel: NotificationChannel | 'BROWSER',
    type: NotificationType,
    enabled: boolean,
  ) => {
    // Handle browser notifications separately
    if (channel === 'BROWSER') {
      const browserTypeKey = getBrowserTypeKey(type)
      if (browserTypeKey) {
        updateNotificationTypes({ [browserTypeKey]: enabled })
      }
      return
    }

    const key = `${channel}-${type}`
    setPendingChanges(prev => ({
      ...prev,
      [key]: enabled,
    }))
    setHasChanges(true)
  }

  // Save all pending changes
  const saveChanges = async () => {
    if (!hasChanges) return

    try {
      setIsSaving(true)

      const updates: UpdateNotificationPreferenceDto[] = Object.entries(
        pendingChanges,
      ).map(([key, enabled]) => {
        const [channel, type] = key.split('-')
        return {
          channel: channel as NotificationChannel,
          type: type as NotificationType,
          enabled,
        }
      })

      if (updateMultiplePreferences) {
        await updateMultiplePreferences({ preferences: updates })
      } else {
        await notificationsApi.updateMultiplePreferences({
          preferences: updates,
        })
      }

      // Reload preferences to get fresh data
      await loadPreferences()

      // Clear pending changes
      setPendingChanges({})
      setHasChanges(false)

      toast.success('Notification preferences updated successfully')
    } catch (error) {
      logger.error('Error saving notification preferences:', error)
      toast.error('Failed to save notification preferences')
    } finally {
      setIsSaving(false)
    }
  }

  // Reset to defaults
  const resetToDefaults = async () => {
    try {
      setIsSaving(true)
      if (resetPreferences) {
        await resetPreferences()
      } else {
        await notificationsApi.setDefaultPreferences()
      }
      await loadPreferences()
      setPendingChanges({})
      setHasChanges(false)
      toast.success('Notification preferences reset to defaults')
    } catch (error) {
      logger.error('Error resetting preferences:', error)
      toast.error('Failed to reset preferences')
    } finally {
      setIsSaving(false)
    }
  }

  // Test notification
  const sendTestNotification = async () => {
    setIsTesting(true)
    try {
      if (isTauri()) {
        // Use Tauri native notification
        const { isPermissionGranted, requestPermission, sendNotification } =
          await import('@tauri-apps/plugin-notification')
        const { invoke } = await import('@tauri-apps/api/core')

        let permissionGranted = await isPermissionGranted()
        logger.info('Tauri notification permission granted:', permissionGranted)

        if (!permissionGranted) {
          const permission = await requestPermission()
          permissionGranted = permission === 'granted'
          logger.info(
            'Tauri notification permission after request:',
            permission,
          )
        }

        if (!permissionGranted) {
          toast.error(
            'Notification permission denied. Please enable notifications in System Settings > Notifications > Scrambl',
          )
          return
        }

        await sendNotification({
          title: 'Test Notification',
          body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
        })

        // Also test the badge count
        try {
          await invoke('set_badge_count', { count: 1 })
          logger.info('Badge count set to 1 for testing')
        } catch (badgeError) {
          logger.warn('Failed to set badge count:', badgeError)
        }

        toast.success(
          'Test notification sent! Check your notification center and dock icon badge.',
        )
      } else {
        // Use browser notification
        if (Notification.permission === 'granted') {
          new Notification('Test Notification', {
            body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
            icon: '/favicon.ico',
          })
          toast.success('Test notification sent!')
        } else if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission()
          if (permission === 'granted') {
            new Notification('Test Notification', {
              body: 'This is a test notification from Scrambl. If you see this, notifications are working!',
              icon: '/favicon.ico',
            })
            toast.success('Test notification sent!')
          } else {
            toast.error('Notification permission denied')
          }
        } else {
          toast.error(
            'Notification permission denied. Please enable in browser settings.',
          )
        }
      }
    } catch (error) {
      logger.error('Failed to send test notification:', error)
      toast.error(
        `Failed to send test notification: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setIsTesting(false)
    }
  }

  // Hide business-only notification categories (client-health, resource
  // planning/utilization) for personal accounts — they'd read as broken chrome.
  const PERSONAL_HIDDEN_CATEGORIES = new Set(['Client Health', 'Resources'])
  const visibleNotificationTypes = isPersonal
    ? notificationTypes.filter(
        config => !PERSONAL_HIDDEN_CATEGORIES.has(config.category),
      )
    : notificationTypes

  // Group notification types by category
  const groupedTypes = visibleNotificationTypes.reduce(
    (acc, config) => {
      if (!acc[config.category]) {
        acc[config.category] = []
      }
      acc[config.category].push(config)
      return acc
    },
    {} as Record<string, NotificationTypeConfig[]>,
  )

  if (isLoading) {
    return (
      <div className={cn('mx-auto max-w-4xl space-y-3', className)}>
        {[1, 2].map(i => (
          <section
            key={i}
            className="overflow-hidden rounded-xl border border-border/60 bg-card"
          >
            <div className="h-9 animate-pulse border-b border-border/60 bg-muted/40" />
            <div className="space-y-3 p-4">
              <div className="h-4 w-2/5 animate-pulse rounded bg-muted" />
              <div className="h-12 animate-pulse rounded bg-muted/50" />
              <div className="h-12 animate-pulse rounded bg-muted/50" />
            </div>
          </section>
        ))}
      </div>
    )
  }

  // Browser/desktop notification setup state
  const needsPermissionSetup =
    browserSupported &&
    (permissionStatus !== 'granted' ||
      (permissionStatus === 'granted' && !browserPrefs.enabled))
  const browserChannelDisabled =
    !browserSupported || !browserPrefs.enabled || permissionStatus !== 'granted'

  return (
    <div className={cn('mx-auto max-w-4xl space-y-3 pb-20', className)}>
      {/* ----------------------------------------------------------------- */}
      {/* Permission banner (conditional)                                   */}
      {/* ----------------------------------------------------------------- */}
      {showChannelSettings && needsPermissionSetup && (
        <section className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
          <div className="flex items-start gap-3 p-4">
            <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <WarningCircleIcon className="h-4 w-4" weight="fill" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {permissionStatus === 'granted'
                  ? `${desktopLabel} notifications are disabled`
                  : `Enable ${desktopLabel.toLowerCase()} notifications`}
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                {permissionStatus === 'denied'
                  ? isDesktopApp
                    ? 'Notifications are blocked at the system level. Enable them in System Settings → Notifications → Scrambl.'
                    : 'Notifications are blocked at the browser level. Enable them in your browser settings.'
                  : permissionStatus === 'granted'
                    ? 'You have permission but notifications are turned off.'
                    : `Get notified even when Scrambl isn't focused.`}
              </p>
            </div>
            {(permissionStatus === 'default' ||
              (permissionStatus === 'granted' && !browserPrefs.enabled)) && (
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    if (permissionStatus === 'granted') {
                      await enableBrowserNotifications()
                    } else {
                      await requestPermission()
                      if (browserPrefs.permissionStatus === 'granted') {
                        await enableBrowserNotifications()
                      }
                    }
                  } catch (error) {
                    logger.error(
                      'Failed to enable browser notifications:',
                      error,
                    )
                  }
                }}
                className="flex-shrink-0"
              >
                Enable
              </Button>
            )}
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Channels & devices                                                */}
      {/* ----------------------------------------------------------------- */}
      {showChannelSettings && (
        <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <header className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/40 px-4 py-2">
            <div className="flex items-center gap-1.5">
              <BellIcon
                className="h-3.5 w-3.5 text-muted-foreground"
                weight="fill"
              />
              <h3 className="text-sm font-semibold">Channels &amp; devices</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={sendTestNotification}
              disabled={isTesting}
              className="h-7 gap-1 px-2 text-xs"
            >
              {isTesting ? (
                <>
                  <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <BellRingingIcon className="h-3.5 w-3.5" />
                  Send test
                </>
              )}
            </Button>
          </header>

          <ul className="divide-y divide-border/60">
            {/* Desktop / browser notifications */}
            <li className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <MonitorIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {desktopLabel} notifications
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {desktopDescription}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={browserPrefs.enabled}
                  onCheckedChange={enabled => {
                    if (enabled) void enableBrowserNotifications()
                    else void disableBrowserNotifications()
                  }}
                  disabled={!browserSupported || permissionStatus !== 'granted'}
                />
              </div>

              {isDesktopApp && (
                <div
                  className={cn(
                    'mt-3 grid gap-1 rounded-md border border-border/60 bg-muted/15 p-1.5 sm:grid-cols-2',
                    !browserPrefs.enabled && 'pointer-events-none opacity-50',
                  )}
                >
                  {[
                    {
                      id: 'desktop-badge',
                      label: 'Show badge count',
                      value: desktopSettings.showBadgeCount ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ showBadgeCount: v }),
                    },
                    {
                      id: 'desktop-group',
                      label: 'Group notifications',
                      value: desktopSettings.groupNotifications ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ groupNotifications: v }),
                    },
                    {
                      id: 'desktop-actions',
                      label: 'Show action buttons',
                      value: desktopSettings.enableActions ?? true,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ enableActions: v }),
                    },
                    {
                      id: 'desktop-persist',
                      label: 'Keep visible until dismissed',
                      value: desktopSettings.persistNotifications ?? false,
                      onChange: (v: boolean) =>
                        void updateDesktopSettings({ persistNotifications: v }),
                    },
                  ].map(item => (
                    <label
                      key={item.id}
                      htmlFor={item.id}
                      className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-background"
                    >
                      <span className="text-xs">{item.label}</span>
                      <Switch
                        id={item.id}
                        checked={item.value}
                        onCheckedChange={item.onChange}
                        disabled={!browserPrefs.enabled}
                      />
                    </label>
                  ))}
                </div>
              )}
            </li>

            {/* Notification sounds */}
            <li className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <SpeakerHighIcon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">Notification sounds</p>
                  <p className="text-xs text-muted-foreground">
                    Play a sound when new messages arrive in threads
                  </p>
                </div>
              </div>
              <Switch
                checked={browserPrefs.playSound ?? true}
                onCheckedChange={enabled =>
                  updateDisplaySettings({ playSound: enabled })
                }
              />
            </li>
          </ul>
        </section>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Notification types matrix                                         */}
      {/* ----------------------------------------------------------------- */}
      <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <header className="flex items-center gap-1.5 border-b border-border/60 bg-muted/40 px-4 py-2">
          <h3 className="text-sm font-semibold">Notification types</h3>
        </header>

        {/* Channel column header */}
        <div
          className="grid items-center gap-2 border-b border-border/60 bg-muted/15 px-4 py-2"
          style={{ gridTemplateColumns: typeGridTemplateColumns }}
        >
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Type
          </div>
          {channels.map(channelConfig => {
            const IconComponent = channelConfig.icon
            const isBrowserChannel = channelConfig.channel === 'BROWSER'
            const isDimmed = isBrowserChannel && browserChannelDisabled
            return (
              <div
                key={channelConfig.channel}
                className={cn(
                  'flex flex-col items-center gap-0.5',
                  isDimmed && 'opacity-50',
                )}
                title={
                  isBrowserChannel && browserChannelDisabled
                    ? `${desktopLabel} notifications are disabled`
                    : channelConfig.description
                }
              >
                <IconComponent
                  className={cn('h-3.5 w-3.5', channelConfig.color)}
                />
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {channelConfig.label}
                </span>
              </div>
            )
          })}
        </div>

        {/* Grouped type rows */}
        <div className="divide-y divide-border/60">
          {Object.entries(groupedTypes).map(([category, types]) => (
            <div key={category}>
              <div className="border-b border-border/60 bg-muted/15 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {category}
              </div>
              <ul className="divide-y divide-border/60">
                {types.map(typeConfig => (
                  <li
                    key={typeConfig.type}
                    className="grid items-center gap-2 px-4 py-2.5 transition-colors hover:bg-muted/20"
                    style={{ gridTemplateColumns: typeGridTemplateColumns }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{typeConfig.label}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {typeConfig.description}
                      </p>
                    </div>
                    {channels.map(channelConfig => {
                      const isAvailable =
                        !typeConfig.channels ||
                        typeConfig.channels.includes(channelConfig.channel)
                      const isBrowserChannel =
                        channelConfig.channel === 'BROWSER'
                      const isDisabled =
                        isBrowserChannel && browserChannelDisabled

                      if (!isAvailable) {
                        return (
                          <div
                            key={`${typeConfig.type}-${channelConfig.channel}`}
                            className="flex justify-center text-muted-foreground/50"
                            title={`${channelConfig.label} notifications are not available for ${typeConfig.label.toLowerCase()}`}
                          >
                            <span aria-hidden="true">—</span>
                            <span className="sr-only">
                              {channelConfig.label} notifications are not
                              available for {typeConfig.label.toLowerCase()}
                            </span>
                          </div>
                        )
                      }

                      return (
                        <div
                          key={`${typeConfig.type}-${channelConfig.channel}`}
                          className="flex justify-center"
                        >
                          <Switch
                            aria-label={`${channelConfig.label} notifications for ${typeConfig.label}`}
                            checked={getPreferenceValue(
                              channelConfig.channel,
                              typeConfig.type,
                            )}
                            onCheckedChange={enabled =>
                              handlePreferenceChange(
                                channelConfig.channel,
                                typeConfig.type,
                                enabled,
                              )
                            }
                            disabled={isDisabled}
                            className={isDisabled ? 'opacity-50' : ''}
                          />
                        </div>
                      )
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Sticky save bar                                                   */}
      {/* ----------------------------------------------------------------- */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="mx-auto flex items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-primary/10">
            <div className="flex items-center gap-2 text-sm">
              <Badge
                variant="outline"
                className="h-5 rounded-md border-primary/30 bg-primary/10 px-1.5 text-[11px] font-normal text-primary"
              >
                Unsaved
              </Badge>
              <span className="text-muted-foreground">
                You have unsaved notification changes
              </span>
            </div>
            <div className="flex flex-shrink-0 gap-2">
              {showResetToDefaults && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetToDefaults}
                  disabled={isSaving}
                >
                  Reset to defaults
                </Button>
              )}
              <Button
                size="sm"
                onClick={saveChanges}
                disabled={isSaving}
                className="min-w-[100px]"
              >
                {isSaving ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckIcon className="mr-2 h-3.5 w-3.5" />
                    Save changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
